import axios from 'axios'

import { exponentialBackOff } from '@climatepartner/unified-api-exponential-backoff'
import { EnvVarNames } from '@climatepartner/unified-api-service/shared'

import { OrderServiceApi } from '../../publish/order/apis/order-service-api'
import {
  OrderServiceCollectiveOrdersApi,
  OrderServiceCollectiveOrdersApiDeleteSubOrderRequest,
  OrderServiceCollectiveOrdersApiGetSubOrderRequest,
} from '../../publish/order/apis/order-service-collective-orders-api'
import {
  OrderServiceIndividualOrdersApi,
  OrderServiceIndividualOrdersApiDeleteIndividualOrderRequest,
  OrderServiceIndividualOrdersApiGetIndividualOrderRequest,
} from '../../publish/order/apis/order-service-individual-orders-api'
import { Configuration } from '../../publish/order/configuration'
import {
  OrderErrorNotFoundCodes,
  OrderInfoRequestHeader,
  OrderRequest,
  OrderStatusEnum,
  OrderSubOrderRequest,
  OrderUpdateIndividualOrderRequest,
  OrderUpdateInfoSubOrderRequest,
} from '../../publish/order/models'
import { setupAxiosInterceptors } from '../axios-interceptor-utils'
import {
  CreateOrderErrorCode,
  UnifiedApiClientError,
  UnifiedApiError,
} from '../types'

const allowedStatuses = [
  OrderStatusEnum.CreateSuccessful,
  OrderStatusEnum.CreateFailed,
  OrderStatusEnum.DeleteSuccessful,
  OrderStatusEnum.DeleteFailed,
  OrderStatusEnum.UpdateSuccessful,
  OrderStatusEnum.UpdateFailed,
]

type GetDeleteOrderResponse = {
  data: { result: { lastActionStatus: OrderStatusEnum } }
}

class OrderBaseService extends OrderServiceApi {
  public async orderActionSync<
    OrderActionFunction extends (...args: any) => Promise<any>,
    GetOrderResponse extends
      | Awaited<
          ReturnType<OrderServiceIndividualOrdersApi['getIndividualOrder']>
        >
      | Awaited<
          ReturnType<OrderServiceCollectiveOrdersApi['getCollectiveOrder']>
        >
      | Awaited<ReturnType<OrderServiceCollectiveOrdersApi['getSubOrder']>>
      | GetDeleteOrderResponse,
  >(
    orderActionFunction: OrderActionFunction,
    orderActionArgs: Parameters<OrderActionFunction>,
    getOrderFunction: (
      createOrderResponse: Awaited<ReturnType<OrderActionFunction>>,
    ) => Promise<GetOrderResponse>,
  ) {
    try {
      // Run order action (ex. create order, update order)
      const createOrderResponse = await orderActionFunction.apply(
        this,
        orderActionArgs,
      )

      // Repeatadly get order until status is final (state success or failure)
      const startingDelay =
        parseInt(process.env[EnvVarNames.SDK_ASYNC_DELAY_START] as string) ||
        100 // 100ms
      const maximumDelay =
        parseInt(process.env[EnvVarNames.SDK_ASYNC_MAX_DELAY] as string) ||
        1000 * 4 // 4000ms = 4s
      /**
       * Total possible delay with defaults (14.3s) calculation:
       * 100 + 200 + 400 + 800 + 1600 + 3200 = 6300ms
       * after this the delay would exceed 4000ms (maximumDelay), so from here 4000 gets applied each retry
       * 6300 + 4000 + 4000 = 14300ms = 14.3s
       */
      const numberOfAttempts =
        parseInt(process.env[EnvVarNames.SDK_ASYNC_MAX_ATTEMPTS] as string) ||
        10

      const getOrderResponse = await exponentialBackOff(
        async () => {
          const getOrderResponse = await getOrderFunction(createOrderResponse)

          if (
            !getOrderResponse?.data?.result?.lastActionStatus ||
            !allowedStatuses.includes(
              getOrderResponse?.data?.result?.lastActionStatus,
            )
          ) {
            throw new UnifiedApiClientError(
              CreateOrderErrorCode.REQUEST_TIMED_OUT,
              'Order processing did not finish in time',
            )
          }

          return getOrderResponse
        },
        {
          numberOfAttempts,
          startingDelay,
          maximumDelay,
        },
      )

      return getOrderResponse
    } catch (e) {
      if (e instanceof UnifiedApiError || e instanceof UnifiedApiClientError) {
        throw e
      }
      throw new UnifiedApiClientError(
        CreateOrderErrorCode.INTERNAL_ERROR,
        (e as Error).message,
      )
    }
  }

  public async pollDeletedOrder(
    getOrderFunction: () => Promise<any>,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
  ): Promise<GetDeleteOrderResponse> {
    try {
      const result = await getOrderFunction()
      if (
        result.data.result.lastActionStatus === OrderStatusEnum.DeleteFailed
      ) {
        return result
      }
    } catch (error) {
      if (error instanceof UnifiedApiError) {
        if (error.code === OrderErrorNotFoundCodes.OrderDeleted) {
          return {
            data: {
              result: {
                lastActionStatus: OrderStatusEnum.DeleteSuccessful,
              },
            },
          }
        }
      }
      throw error
    }
  }
}

class OrderServiceIndividualOrdersBase extends OrderServiceIndividualOrdersApi {
  constructor(
    configuration?: Configuration,
    basePath?: string,
    private getTokenFn?: () => Promise<string>,
  ) {
    super(configuration, basePath, axios.create())
    setupAxiosInterceptors(this.axios, this.getTokenFn)
  }

  protected orderBaseService = new OrderBaseService()
}

class OrderServiceCollectiveOrdersBase extends OrderServiceCollectiveOrdersApi {
  constructor(
    configuration?: Configuration,
    basePath?: string,
    private getTokenFn?: () => Promise<string>,
  ) {
    super(configuration, basePath, axios.create())
    setupAxiosInterceptors(this.axios, this.getTokenFn)
  }

  protected orderBaseService = new OrderBaseService()
}

export class OrderServiceIndividualOrders extends OrderServiceIndividualOrdersBase {
  constructor(protected basePath: string, getTokenFn?: () => Promise<string>) {
    super(new Configuration({ basePath: basePath }), basePath, getTokenFn)
  }

  async createIndividualOrderSync(body: OrderRequest) {
    return this.orderBaseService.orderActionSync(
      this.createIndividualOrder.bind(this),
      [{ orderRequest: body }],
      createOrderResponse =>
        this.getIndividualOrder({
          orderId: createOrderResponse.data.result.orderId,
        }),
    )
  }

  async updateIndividualOrderSync(
    pathParameters: OrderServiceIndividualOrdersApiGetIndividualOrderRequest,
    body: OrderUpdateIndividualOrderRequest,
  ) {
    return this.orderBaseService.orderActionSync(
      this.updateIndividualOrder.bind(this),
      [
        {
          orderId: pathParameters.orderId,
          orderUpdateIndividualOrderRequest: body,
        },
      ],
      updateIndividualOrderResponse =>
        this.getIndividualOrder({
          orderId: updateIndividualOrderResponse.data.result.orderId,
        }),
    )
  }

  async deleteIndividualOrderSync(
    pathParameters: OrderServiceIndividualOrdersApiDeleteIndividualOrderRequest,
  ) {
    return this.orderBaseService.orderActionSync(
      this.deleteIndividualOrder.bind(this),
      [pathParameters],
      deleteIndividualOrderResponse =>
        this.orderBaseService.pollDeletedOrder(() =>
          this.getIndividualOrder({
            orderId: deleteIndividualOrderResponse.data.result.orderId,
          }),
        ),
    )
  }
}

export class OrderServiceCollectiveOrders extends OrderServiceCollectiveOrdersBase {
  constructor(protected basePath: string, getTokenFn?: () => Promise<string>) {
    super(new Configuration({ basePath: basePath }), basePath, getTokenFn)
  }

  async createCollectiveOrderSync(body: OrderInfoRequestHeader) {
    return this.orderBaseService.orderActionSync(
      this.createCollectiveOrder.bind(this),
      [{ orderInfoRequestHeaderCreate: body }],
      createOrderResponse =>
        this.getCollectiveOrder({
          collectiveOrderId: createOrderResponse.data.result.collectiveOrderId,
        }),
    )
  }

  async createSubOrderSync(body: OrderSubOrderRequest) {
    return this.orderBaseService.orderActionSync(
      this.createSubOrder.bind(this),
      [{ orderSubOrderRequest: body }],
      createOrderResponse =>
        this.getSubOrder({
          orderId: createOrderResponse.data.result.orderId,
        }),
    )
  }

  async updateCollectiveOrderSync(
    pathParameters: { collectiveOrderId: string },
    body: OrderInfoRequestHeader,
  ) {
    return this.orderBaseService.orderActionSync(
      this.updateCollectiveOrder.bind(this),
      [
        {
          collectiveOrderId: pathParameters.collectiveOrderId,
          orderInfoRequestHeader: body,
        },
      ],
      updateCollectiveOrderResponse =>
        this.getCollectiveOrder({
          collectiveOrderId:
            updateCollectiveOrderResponse.data.result.collectiveOrderId,
        }),
    )
  }

  async updateSubOrderSync(
    pathParameters: OrderServiceCollectiveOrdersApiGetSubOrderRequest,
    body: OrderUpdateInfoSubOrderRequest,
  ) {
    return this.orderBaseService.orderActionSync(
      this.updateSubOrder.bind(this),
      [
        {
          orderId: pathParameters.orderId,
          orderUpdateInfoSubOrderRequest: body,
        },
      ],
      updateSubOrderResponse =>
        this.getSubOrder({
          orderId: updateSubOrderResponse.data.result.orderId,
        }),
    )
  }

  async deleteSubOrderSync(
    pathParameters: OrderServiceCollectiveOrdersApiDeleteSubOrderRequest,
  ) {
    return this.orderBaseService.orderActionSync(
      this.deleteSubOrder.bind(this),
      [pathParameters],
      deleteSubOrderResponse =>
        this.orderBaseService.pollDeletedOrder(() =>
          this.getSubOrder({
            orderId: deleteSubOrderResponse.data.orderId,
          }),
        ),
    )
  }
}
